/**
 * API Client Service
 *
 * Handles all HTTP requests to the backend API with:
 * - Automatic auth token injection
 * - Token refresh on 401 errors
 * - Request/response error handling
 * - Typed responses
 */

import { useAuthStore } from '../stores/auth.store';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const USE_MOCK = import.meta.env.VITE_USE_MOCK_AUTH !== 'false';

// Types
export interface ApiResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

export interface ApiError {
  message: string;
  code: string;
  status: number;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string = 'API_ERROR'
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Token management
let authToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Refresh the auth token using the refresh token
 * Returns the new access token or null if refresh fails
 */
async function refreshAuthToken(): Promise<string | null> {
  // If already refreshing, wait for that to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      if (USE_MOCK) {
        // Mock: just return a fake refreshed token
        const mockToken = `mock-token-${Date.now()}`;
        authToken = mockToken;
        return mockToken;
      }

      // TODO: Real implementation would call Cognito refresh endpoint
      // const { tokens } = await fetchAuthSession({ forceRefresh: true });
      // authToken = tokens?.accessToken?.toString() || null;
      // return authToken;

      return null;
    } catch {
      // Refresh failed - clear auth state
      authToken = null;
      useAuthStore.getState().signOut();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Build request headers with auth token
 */
function buildHeaders(customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  return headers;
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  let data: T;

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else {
    data = (await response.text()) as unknown as T;
  }

  if (!response.ok) {
    const errorData = data as unknown as { message?: string; code?: string };
    throw new ApiClientError(
      errorData?.message || `Request failed with status ${response.status}`,
      response.status,
      errorData?.code || 'API_ERROR'
    );
  }

  return {
    data,
    status: response.status,
    ok: true,
  };
}

/**
 * Core fetch wrapper with auth and retry logic
 */
async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
  retry = true
): Promise<ApiResponse<T>> {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const response = await fetch(fullUrl, {
    ...options,
    headers: buildHeaders(options.headers),
  });

  // Handle 401 Unauthorized - try to refresh token
  if (response.status === 401 && retry && authToken) {
    const newToken = await refreshAuthToken();

    if (newToken) {
      // Retry with new token
      return fetchWithAuth<T>(url, options, false);
    }

    // Refresh failed - redirect to login
    useAuthStore.getState().signOut();
    throw new ApiClientError(
      'Session expired. Please sign in again.',
      401,
      'SESSION_EXPIRED'
    );
  }

  return handleResponse<T>(response);
}

// ============== API Methods ==============

/**
 * GET request
 */
export async function get<T>(
  url: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  return fetchWithAuth<T>(url, { ...options, method: 'GET' });
}

/**
 * POST request
 */
export async function post<T>(
  url: string,
  body?: unknown,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  return fetchWithAuth<T>(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request
 */
export async function put<T>(
  url: string,
  body?: unknown,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  return fetchWithAuth<T>(url, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request
 */
export async function patch<T>(
  url: string,
  body?: unknown,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  return fetchWithAuth<T>(url, {
    ...options,
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request
 */
export async function del<T>(
  url: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  return fetchWithAuth<T>(url, { ...options, method: 'DELETE' });
}

// ============== Convenience API object ==============

export const api = {
  get,
  post,
  put,
  patch,
  delete: del,
  setToken: setAuthToken,
  getToken: getAuthToken,
};

export default api;
